import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Guided Routine Discovery — a signature sticky, scroll-driven editorial
// moment. The left rail (≈33%) stays fixed while the right column (≈67%)
// scrolls through Riversol's four official routine stages. As each large
// media panel crosses the viewport centre it promotes its stage on the
// sticky stepper. This is NOT the literal "Your Routine, Step by Step"
// section — it is an immersive introduction to how the products layer.
// All names, copy, media and hrefs are pulled directly from riversol.com.
const STEPS = [
  {
    n: "01",
    stage: "Preparation",
    benefit: "Gentle, purifying cleansers to hydrate & restore.",
    image:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/2pjyupsq_image.png",
    cover: true,
    count: 6,
    collection: "https://www.riversol.com/collections/step-1-preparation",
    tint: "#EDF3F3",
  },
  {
    n: "02",
    stage: "Treatment",
    benefit: "Targeted serums & treatments to soften fine lines & texture.",
    image:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/5z1xcd6x_image.png",
    cover: true,
    count: 12,
    collection: "https://www.riversol.com/collections/step-2-treatment",
    tint: "#F1EEE8",
  },
  {
    n: "03",
    stage: "Hydration",
    benefit: "Moisturizers for brighter, smoother, deeply hydrated skin.",
    image:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/5vir9p0b_image.png",
    cover: true,
    count: 8,
    collection: "https://www.riversol.com/collections/step-3-hydration",
    tint: "#EFF2EF",
  },
  {
    n: "04",
    stage: "Protection",
    benefit: "100% mineral SPF with niacinamide to shield & protect daily.",
    image:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/nrcxef2p_image.png",
    cover: true,
    count: 2,
    collection: "https://www.riversol.com/collections/step-4-protection",
    tint: "#F3F0EC",
  },
];

const Panel = ({ item, index, setActive }) => {
  const ref = useRef(null);
  const centered = useInView(ref, { margin: "-45% 0px -45% 0px" });
  const reveal = useInView(ref, { once: true, margin: "-12% 0px -12% 0px" });

  useEffect(() => {
    if (centered) setActive(index);
  }, [centered, index, setActive]);

  return (
    <motion.article
      ref={ref}
      id={`routine-step-${index}`}
      data-testid={`guided-step-${index}`}
      initial={{ opacity: 0, y: 48 }}
      animate={reveal ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="scroll-mt-28 min-h-[72vh] lg:min-h-[84vh] flex flex-col justify-center py-10 lg:py-12"
    >
      <div
        className="relative overflow-hidden rounded-sm h-[46vh] sm:h-[54vh] lg:h-[62vh] max-h-[600px]"
        style={{ backgroundColor: item.tint }}
      >
        <img
          src={item.image}
          alt={`${item.stage} collection`}
          className={
            item.cover
              ? "w-full h-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.04]"
              : "w-full h-full object-contain p-8 sm:p-10 lg:p-12 transition-transform duration-[1400ms] ease-out hover:scale-[1.04]"
          }
          draggable={false}
          loading="lazy"
        />
        <div className="absolute top-6 left-6 lg:top-8 lg:left-8 flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-900 bg-white/90 backdrop-blur-sm px-3 py-1.5">
            {item.n} · {item.stage}
          </span>
        </div>
      </div>

      <div className="mt-6 lg:mt-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
        <div className="max-w-[30rem]">
          <h3 className="text-[30px] lg:text-[40px] leading-[1.04] tracking-[-0.02em] text-neutral-900">
            {item.stage}
          </h3>
          <p className="mt-4 text-[15px] lg:text-[16px] text-neutral-600 leading-relaxed">
            {item.benefit}
          </p>
        </div>
        <div className="flex items-center gap-6 shrink-0">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-500 tabular-nums">
            {item.count} items
          </span>
          <a
            href={item.collection}
            data-testid={`guided-view-${index}`}
            className="group inline-flex items-center gap-1.5 text-[12px] tracking-[0.16em] uppercase font-medium text-riversol-blue border-b border-riversol-blue/40 pb-1 hover:border-riversol-blue transition-colors"
          >
            Shop {item.stage}
            <ArrowRight
              size={14}
              strokeWidth={1.8}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

export const GuidedDiscovery = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [active, setActive] = useState(0);
  const onActive = useCallback((i) => setActive(i), []);

  return (
    <section
      ref={ref}
      className="bg-white pt-10 lg:pt-14 pb-24 lg:pb-32"
      data-testid="home-guided-discovery"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-y-0 lg:gap-x-16 items-start">
        {/* Sticky left rail */}
        <div className="lg:col-span-5 lg:self-start lg:sticky lg:top-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between">
              <span className="hidden font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
                The Riversol routine
              </span>
              <span
                className="font-mono text-[11px] tracking-[0.2em] text-neutral-400 tabular-nums"
                data-testid="guided-progress-count"
              >
                {STEPS[active].n} / {STEPS.length.toString().padStart(2, "0")}
              </span>
            </div>

            <h2 className="mt-5 text-[34px] sm:text-[40px] lg:text-[50px] xl:text-[56px] leading-[1.02] tracking-[-0.02em] text-neutral-900">
              Four steps, made to{" "}
              <span className="italic text-riversol-blue">work together.</span>
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed text-neutral-600 max-w-[400px]">
              A dermatologist-developed regimen that moves from preparation to
              protection — each formula chosen to support the next.
            </p>

            {/* Stepper */}
            <ul className="mt-10 border-t border-neutral-200" data-testid="guided-stepper">
              {STEPS.map((s, i) => {
                const isActive = i === active;
                return (
                  <li key={s.n} className="border-b border-neutral-200">
                    <a
                      href={`#routine-step-${i}`}
                      data-testid={`guided-stepper-${i}`}
                      className="group flex items-center gap-4 py-4 outline-none"
                    >
                      <span
                        className={`relative h-8 w-px shrink-0 overflow-hidden bg-neutral-200`}
                        aria-hidden
                      >
                        <motion.span
                          className="absolute inset-x-0 top-0 bg-riversol-blue"
                          animate={{ height: isActive ? "100%" : "0%" }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        />
                      </span>
                      <span
                        className={`font-mono text-[11px] tracking-[0.2em] tabular-nums transition-colors duration-300 ${
                          isActive ? "text-riversol-blue" : "text-neutral-400"
                        }`}
                      >
                        {s.n}
                      </span>
                      <span
                        className={`text-[15px] lg:text-[17px] tracking-[-0.01em] transition-colors duration-300 ${
                          isActive
                            ? "text-neutral-900"
                            : "text-neutral-400 group-hover:text-neutral-600"
                        }`}
                      >
                        {s.stage}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="mt-10 flex flex-col gap-4 items-start">
              <a
                href="https://www.riversol.com/pages/skin-analyzer"
                data-testid="guided-analyzer-cta"
                className="group inline-flex items-center gap-2 bg-riversol-blue text-white rounded-full px-7 py-3.5 text-[13px] tracking-[0.14em] uppercase font-medium hover:bg-riversol-blueLight transition-colors"
              >
                Take the Skin Analyzer
                <ArrowRight
                  size={16}
                  strokeWidth={1.8}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
              <a
                href="https://www.riversol.com/collections/all"
                data-testid="guided-shop-all"
                className="text-[12px] tracking-[0.16em] uppercase font-medium text-neutral-900 underline underline-offset-[6px] decoration-neutral-300 hover:decoration-riversol-blue transition-colors"
              >
                Shop all products
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scrolling right column */}
        <div className="lg:col-span-7 lg:col-start-6">
          {STEPS.map((s, i) => (
            <Panel key={s.n} item={s} index={i} setActive={onActive} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default GuidedDiscovery;
